import { useForm } from "@tanstack/react-form";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { Textarea } from "@/core/components/ui/textarea";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useSimulateAuditEvent } from "@/features/admin/api/use-simulate-audit-event";
import type { SimulateAuditEventRequestDto } from "@/features/admin/api/dto";
import { useAdminTranslation } from "@/features/admin/i18n/use-admin-translation";
import {
  createSimulateAuditEventSchema,
  type SimulateAuditEventFormValues,
} from "@/features/admin/schemas/simulate-audit-event.schema";

const EMPTY_VALUES: SimulateAuditEventFormValues = {
  eventId: "",
  topic: "",
  actorKeycloakId: "",
  action: "",
  resourceType: "",
  resourceId: "",
  sourceService: "",
  beforeState: "",
  afterState: "",
  ipAddress: "",
};

/**
 * ⚙️ Dialog de simulation d'un événement d'audit
 * (`POST /AuditLogs/simulate-audit-event`) — point d'entrée de remplacement
 * du consommateur Kafka d'audit, non encore câblé. Réservé à
 * flashpay-superadmin (visibilité gérée par la page via `RequireRole`).
 */
export function SimulateAuditEventDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useAdminTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : schéma zod traduit reconstruit sur changement de langue (règle projet).
  const schema = useMemo(() => createSimulateAuditEventSchema(), [i18n.language]);

  const simulate = useSimulateAuditEvent();
  const pending = simulate.isPending;

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      const body: SimulateAuditEventRequestDto = {
        eventId: parsed.data.eventId?.trim() || undefined,
        topic: parsed.data.topic.trim(),
        actorKeycloakId: parsed.data.actorKeycloakId.trim(),
        action: parsed.data.action.trim(),
        resourceType: parsed.data.resourceType.trim(),
        resourceId: parsed.data.resourceId.trim(),
        sourceService: parsed.data.sourceService.trim(),
        beforeState: parsed.data.beforeState?.trim() || undefined,
        afterState: parsed.data.afterState?.trim() || undefined,
        ipAddress: parsed.data.ipAddress?.trim() || undefined,
      };
      simulate.mutate(body, {
        onSuccess: () => {
          notifSilence(t("admin.audit.simulate.feedback.sent"));
          onOpenChange(false);
        },
        onError: (error) => notifSilenceError(error.message),
      });
    },
  });

  const fieldError =
    (name: keyof SimulateAuditEventFormValues) =>
    (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  const textField = (
    name: keyof SimulateAuditEventFormValues,
    labelKey: string,
    placeholderKey: string,
    options?: { mono?: boolean; span?: boolean; textarea?: boolean }
  ) => (
    <form.Field name={name} validators={{ onChange: fieldError(name) }}>
      {(field) => (
        <div className={`space-y-1.5${options?.span ? " sm:col-span-2" : ""}`}>
          <Label htmlFor={`sim-${name}`}>{t(labelKey)}</Label>
          {options?.textarea ? (
            <Textarea
              id={`sim-${name}`}
              className="font-mono notranslate"
              placeholder={t(placeholderKey)}
              value={(field.state.value as string) ?? ""}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
            />
          ) : (
            <Input
              id={`sim-${name}`}
              className={options?.mono ? "font-mono notranslate" : undefined}
              placeholder={t(placeholderKey)}
              value={(field.state.value as string) ?? ""}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
            />
          )}
          {field.state.meta.errors.length > 0 && (
            <p className="text-sm text-destructive">
              {field.state.meta.errors[0]}
            </p>
          )}
        </div>
      )}
    </form.Field>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("admin.audit.simulate.title")}</DialogTitle>
          <DialogDescription>
            {t("admin.audit.simulate.description")}
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          {textField(
            "eventId",
            "admin.audit.simulate.eventId",
            "admin.audit.simulate.eventIdPlaceholder",
            { mono: true, span: true }
          )}
          {textField(
            "topic",
            "admin.audit.simulate.topic",
            "admin.audit.simulate.topicPlaceholder",
            { mono: true }
          )}
          {textField(
            "actorKeycloakId",
            "admin.audit.simulate.actorKeycloakId",
            "admin.audit.simulate.actorKeycloakIdPlaceholder",
            { mono: true }
          )}
          {textField(
            "action",
            "admin.audit.simulate.action",
            "admin.audit.simulate.actionPlaceholder",
            { mono: true }
          )}
          {textField(
            "sourceService",
            "admin.audit.simulate.sourceService",
            "admin.audit.simulate.sourceServicePlaceholder",
            { mono: true }
          )}
          {textField(
            "resourceType",
            "admin.audit.simulate.resourceType",
            "admin.audit.simulate.resourceTypePlaceholder",
            { mono: true }
          )}
          {textField(
            "resourceId",
            "admin.audit.simulate.resourceId",
            "admin.audit.simulate.resourceIdPlaceholder",
            { mono: true }
          )}
          {textField(
            "beforeState",
            "admin.audit.simulate.beforeState",
            "admin.audit.simulate.beforeStatePlaceholder",
            { textarea: true }
          )}
          {textField(
            "afterState",
            "admin.audit.simulate.afterState",
            "admin.audit.simulate.afterStatePlaceholder",
            { textarea: true }
          )}
          {textField(
            "ipAddress",
            "admin.audit.simulate.ipAddress",
            "admin.audit.simulate.ipAddressPlaceholder",
            { mono: true }
          )}

          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <ButtonSpinner /> : null}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
